import {
  Box,
  Divider,
  Typography,
} from "@mui/material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";


/* ステップアイコン（通知先表示・通知ステータスの見方で共用） */
// ステップ表示の見た目
//  - active : 緑塗り + 白アイコン
//  - waiting: 薄緑塗り + 緑枠 + 緑アイコン（送信待ち）
//  - idle   : 灰枠 + 灰アイコン
//  - skipped: 灰破線枠 + 薄灰アイコン（キャンセル）
//  - error  : 赤アイコンのみ（送信失敗）
export type StepVariant = "active" | "waiting" | "idle" | "skipped" | "error";
export type LineVariant = "active" | "idle" | "skipped";

const STEP_SIZE = 18;
const STEP_ICON_SIZE = 12;
export const ACTIVE_COLOR = "#3f6b2f";
const CHECK_SIZE = 12;
// ステップアイコンと丸の大きさを揃えたチェックのサイズ（CheckCircleIcon は内側に余白があるため大きめに指定）
export const CHECK_SIZE_LARGE = 22;

// メール通知の遅延時間
// Lambdaの環境変数 COMMUNICATION_NOTICE_DELAY_MINUTES と合わせること
const NOTICE_DELAY_MINUTES = 1;
export const NOTICE_DELAY_LABEL = `${NOTICE_DELAY_MINUTES}min`;
export const NOTICE_DELAY_LABEL_JA = `${NOTICE_DELAY_MINUTES}分`;


// ステップのアイコンを1つ描画
export const renderStep = (
  Icon: typeof TimerOutlinedIcon | null,   // 丸の中に描くMuiIcon（時計 or メール or 目）, nullは凡例用の中身なし
  variant: StepVariant,   // 見た目の種類
  badge?: string     // 右下につけるラベル（時計のみ）
) => {

  // 送信失敗の場合は丸なしのIconをそのまま返す
  if (variant === "error") {
    return (
      <ReportOutlinedIcon sx={{ fontSize: STEP_SIZE + 2, color: "error.main" }} />
    );
  }

  // variantに応じたスタイルを取得
  const styles = {
    active: { bgcolor: ACTIVE_COLOR, border: `1px solid ${ACTIVE_COLOR}`, color: "#fff" },
    waiting: { bgcolor: "#e6efe2", border: `1px solid ${ACTIVE_COLOR}`, color: ACTIVE_COLOR },
    idle: { bgcolor: "background.paper", border: "1px solid", borderColor: "grey.400", color: "grey.500" },
    skipped: { bgcolor: "background.paper", border: "1px dashed", borderColor: "grey.400", color: "grey.400" },
  }[variant];

  // バッジの色 
  const badgeColor = (variant === "skipped" || variant === "idle") 
    ? "grey.400" 
    : ACTIVE_COLOR;

  return (
    <Box
      sx={{
        width: STEP_SIZE,
        height: STEP_SIZE,
        borderRadius: "50%",   // 丸を描画
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        position: "relative",
        ...styles,   // variantに応じたstyleの埋め込み
      }}
    >
      {Icon && <Icon sx={{ fontSize: STEP_ICON_SIZE, color: "inherit" }} />}
      {badge && (
        // アイコン右下の小さなラベル（時計アイコンの"1min"）
        <Box
          component="span"
          sx={{
            position: "absolute",
            right: -6,
            bottom: -4,
            px: "2px",
            fontSize: 8,
            lineHeight: "9px",
            fontWeight: 700,
            whiteSpace: "nowrap",
            borderRadius: "4px",
            transform: "scale(0.75)",   // ブラウザの最小フォントサイズ制限を受けないようtransformで指定
            transformOrigin: "right bottom",  // transformする際に右下基準で縮める
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: badgeColor,
            color: badgeColor,
          }}
        >
          {badge}
        </Box>
      )}
    </Box>
  );
};

// ステップ間の繋ぎ線の描画
const renderLine = (variant: LineVariant, width = 5) => (
  <Box
    sx={{
      width,
      mx: "1px",
      borderTop: (variant === "skipped") ? "1px dashed" : "2px solid",
      borderColor:
        (variant === "active")
          ? ACTIVE_COLOR
          : (variant === "skipped")
            ? "grey.400"
            : "grey.300",
    }}
  />
);


// メール通知状況+既読状況の判定結果
export type NoticeStatusInfo = {
  timer: StepVariant;
  mail: StepVariant;
  eye: StepVariant;
  line1: LineVariant;
  line2: LineVariant;
  stepLabels: [string, string, string];   // 各ステップの文言（時計・メール・目）
  label: string;          // 状態の説明文言
  hasError: boolean;      // メール送信失敗か（チェックマークに！を付ける）
};

// メール通知状況+既読状況を判定
export const getNoticeStatus = (status: string, isConfirmed: boolean): NoticeStatusInfo => {
  
  const key = String(status || "").toLowerCase();
  const eye: StepVariant = isConfirmed ? "active" : "idle";
  const eyeLabel = isConfirmed ? "スレッド\n既読" : "スレッド\n未読";
  const timerLabel = `送信予約\n(${NOTICE_DELAY_LABEL})`;

  let timer: StepVariant;
  let mail: StepVariant;
  let line1: LineVariant;
  let line2: LineVariant;
  let mailLabel: string;
  let label: string;

  if (key === "pending" && !isConfirmed) {
    // メール送信状況：送信待ち（未読）
    timer = "waiting";
    mail = "idle";
    line1 = "idle";
    line2 = "idle";
    mailLabel = "メール送信\n待ち";
    label = `メール送信待ち（${NOTICE_DELAY_LABEL}後）／スレッド未読`;

  } else if (key === "sending" || key === "sent") {
    // メール送信状況：送信中/送信済み
    timer = "active";
    mail = "active";
    line1 = "active";

    // 既読か否かで表示わけ
    line2 = isConfirmed ? "active" : "idle";
    mailLabel = "メール\n通知済み";
    label = isConfirmed
      ? "メール通知済み／スレッド既読"
      : "メール通知済み／スレッド未読";

  } else if (key === "failed") {
    // メール送信状況：送信失敗
    timer = "active";
    mail = "error";
    line1 = "active";
    line2 = "idle";
    mailLabel = "メール\n送信失敗";
    label = isConfirmed
      ? "メール送信失敗／スレッド既読"
      : "メール送信失敗／スレッド未読";

  } else {
    // メール送信状況：キャンセル・未送信・想定外の値
    // pending かつ既読もここ（送信時に confirm=1 で canceled になるため）
    timer = "skipped";
    mail = "skipped";
    line1 = "skipped";
    line2 = "skipped";
    mailLabel = "メール\n送信なし";
    label = isConfirmed
      ? "メール送信前にスレッド既読"
      : "メール未送信／スレッド未読";
  }

  return {
    timer,
    mail,
    eye,
    line1,
    line2,
    stepLabels: [timerLabel, mailLabel, eyeLabel],
    label,
    hasError: key === "failed",
  };
};


// 既読/未読のチェックマークを描画（既読は緑・未読はグレー、送信失敗時は右上に！を付ける）
export const renderStatusCheck = (
  isConfirmed: boolean,
  hasError: boolean,
  size = CHECK_SIZE,
) => (
  <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
    <CheckCircleIcon
      sx={{ fontSize: size, color: isConfirmed ? ACTIVE_COLOR : "grey.400" }}
    />
    {hasError && (
      <ErrorIcon
        sx={{
          position: "absolute",
          // ！の大きさ・位置はチェックのサイズに比例させる（12px のとき top:-4, right:-5, fontSize:9）
          top: -size / 3,
          right: -size * 5 / 12,
          fontSize: size * 0.75,
          color: "error.main",
          bgcolor: "background.paper",   // 背景で縁取りしてチェックと重なっても見えるように
          borderRadius: "50%",
        }}
      />
    )}
  </Box>
);


// マウスオーバー時の詳細（3アイコン+状態）を描画
export const renderStatusDetail = (userName: string, info: NoticeStatusInfo) => {
  const steps: { Icon: typeof TimerOutlinedIcon; variant: StepVariant }[] = [
    { Icon: TimerOutlinedIcon, variant: info.timer },
    { Icon: MailOutlineIcon, variant: info.mail },
    { Icon: VisibilityOutlinedIcon, variant: info.eye },
  ];
  const lines = [info.line1, info.line2];

  return (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" component="div" sx={{ fontWeight: 700, mb: 1 }}>
        {userName}さんの通知ステータス
      </Typography>

      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        {steps.map(({ Icon, variant }, idx) => (
          <Box key={idx} sx={{ display: "contents" }}>
            {idx > 0 && (
              // 繋ぎ線はアイコンの高さの中央に合わせる
              <Box sx={{ height: STEP_SIZE, display: "flex", alignItems: "center" }}>
                {renderLine(lines[idx - 1], 16)}
              </Box>
            )}
            <Box
              sx={{
                minWidth: 72,   // 短い文言でもアイコンの間隔を保つ（長い文言は幅を広げる）
                px: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {renderStep(Icon, variant)}
              <Typography
                variant="caption"
                sx={{ fontSize: 11, lineHeight: 1.3, textAlign: "center", whiteSpace: "pre" }}  // 自動で折り返さず、\n の位置だけで改行
              >
                {info.stepLabels[idx]}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Typography
        variant="caption"
        component="div"
        sx={{ fontWeight: 700, color: "text.secondary" }}
      >
        {info.label}
      </Typography>
    </Box>
  );
};
